// client/src/api/errorTrackingApi.ts

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  FrontendErrorPayload,
  GetRecentErrorsResponse,
  GetErrorStatsResponse,
  ErrorEventDetail,
  ErrorEventSource,
  ErrorEventSeverity,
} from '../types/api';

// Standalone function for logging frontend errors (doesn't use RTK Query)
// This allows error logging to work even if Redux store is unavailable
export async function logFrontendError(payload: FrontendErrorPayload): Promise<boolean> {
  try {
    const response = await fetch('/api/errors/frontend', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    return response.ok;
  } catch {
    // Silently fail - we don't want error logging to cause more errors
    console.error('Failed to log frontend error');
    return false;
  }
}

// Capture and log an error with automatic context extraction
export function captureError(
  error: Error,
  options?: {
    route?: string;
    extra?: Record<string, unknown>;
  }
): void {
  const payload: FrontendErrorPayload = {
    message: error.message || 'Unknown error',
    stack: error.stack,
    route: options?.route || window.location.pathname,
    context: {
      browser: navigator.userAgent,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      extra: options?.extra,
    },
  };

  // Fire and forget - don't await
  logFrontendError(payload);
}

// RTK Query API for admin error viewing
export const errorTrackingApi = createApi({
  reducerPath: 'errorTrackingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/errors',
    credentials: 'include',
  }),
  tagTypes: ['ErrorEvents'],
  endpoints: (builder) => ({
    // Get recent error events
    getRecentErrors: builder.query<
      GetRecentErrorsResponse,
      {
        limit?: number;
        source?: ErrorEventSource;
        severity?: ErrorEventSeverity;
        environment?: string;
      } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        if (params?.source) searchParams.set('source', params.source);
        if (params?.severity) searchParams.set('severity', params.severity);
        if (params?.environment) searchParams.set('environment', params.environment);

        const queryString = searchParams.toString();
        return `/recent${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['ErrorEvents'],
    }),

    // Get error statistics
    getErrorStats: builder.query<GetErrorStatsResponse, void>({
      query: () => '/stats',
      providesTags: ['ErrorEvents'],
    }),

    // Get single error event by ID
    getErrorById: builder.query<{ error: ErrorEventDetail }, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: 'ErrorEvents', id }],
    }),
  }),
});

export const {
  useGetRecentErrorsQuery,
  useGetErrorStatsQuery,
  useGetErrorByIdQuery,
} = errorTrackingApi;
